import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleUserRound, LogIn, UserPlus, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { setPortalAuth, isPortalAuthed } from "@/lib/portalAuth";
import { setAuth as setAdminAuth } from "@/lib/auth";

export default function PortalAuth() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'register' | 'forgot'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", password: "", company_name: "",
  });

  useEffect(() => { if (isPortalAuthed()) nav("/portail", { replace: true }); }, [nav]);

  /** Try admin login first (priority), fallback to entrepreneur portal. */
  const tryAdminThenPortal = async (email, password) => {
    // 1) Admin login attempt
    try {
      const r = await api.post("/admin/login", { email, password });
      if (r?.data?.token) {
        setAdminAuth(r.data.token, r.data.admin?.name || "Admin");
        return { kind: "admin" };
      }
    } catch (_) { /* swallow; fall through to portal */ }
    // 2) Entrepreneur portal login
    const r = await api.post("/portal/login", { email, password });
    if (!r?.data?.token) throw new Error("Identifiants invalides");
    setPortalAuth(r.data.token, r.data.entrepreneur);
    return { kind: "portal" };
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (mode === "forgot") {
      if (!form.email) return toast.error("Email requis");
      setBusy(true);
      try {
        await api.post("/portal/forgot-password", { email: form.email });
        toast.success("Si ce compte existe, un email de réinitialisation a été envoyé.");
        setMode("login");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erreur");
      } finally { setBusy(false); }
      return;
    }
    if (!form.email || !form.password) return toast.error("Email et mot de passe requis");
    setBusy(true);
    try {
      if (mode === "register") {
        if (!form.full_name) { toast.error("Votre nom est requis"); setBusy(false); return; }
        const r = await api.post("/portal/register", form);
        setPortalAuth(r.data.token, r.data.entrepreneur);
        toast.success("Compte créé !");
        nav("/portail");
        return;
      }
      // login mode — auto-detect admin vs entrepreneur
      const out = await tryAdminThenPortal(form.email, form.password);
      if (out.kind === "admin") {
        toast.success("Connecté en admin");
        nav("/admin");
      } else {
        toast.success("Connecté !");
        nav("/portail");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Identifiants invalides");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Header />
      <main
        data-testid="portal-auth-page"
        className="relative pt-40 sm:pt-44 pb-16 min-h-screen bg-gradient-to-br from-[#0A1128] via-[#14213d] to-[#0A1128] text-white overflow-hidden"
      >
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-20 pointer-events-none" style={{ background: "#06B6D4", filter: "blur(120px)" }} />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full opacity-15 pointer-events-none" style={{ background: "#FFDCA8", filter: "blur(140px)" }} />

        <div className="relative max-w-md mx-auto px-4 sm:px-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#06B6D4] text-[#0A1128] mb-3 shadow-xl">
              <CircleUserRound size={28} />
            </div>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#67E8F9]">Espace Entreprise</div>
            <h1 className="display text-2xl sm:text-3xl font-extrabold mt-2 leading-tight">
              {mode === "login" ? "Connexion à votre espace" : mode === "forgot" ? "Mot de passe oublié" : "Créez votre espace"}
            </h1>
            <p className="text-white/70 text-[13px] sm:text-sm mt-2">
              {mode === "login"
                ? "Clients : suivez vos projets, accès & devis. Admin : entrez vos identifiants."
                : mode === "forgot"
                  ? "Entrez votre email — nous vous enverrons un lien de réinitialisation."
                  : "Gérez plusieurs entreprises et l'historique de vos prestations."}
            </p>
          </div>

          <form onSubmit={submit} className="bg-white text-[#0A1128] rounded-3xl p-5 sm:p-6 shadow-2xl space-y-3.5">
            {mode === "register" && (
              <>
                <div><Label>Prénom & Nom *</Label><Input data-testid="portal-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required /></div>
                <div><Label>Téléphone</Label><Input data-testid="portal-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="bg-[#06B6D4]/10 border border-[#06B6D4]/20 rounded-lg p-2.5 text-[11px] text-[#0A1128] flex items-start gap-2">
                  <span className="text-[#06B6D4] font-bold">ℹ</span>
                  <span>Vous créez d'abord votre compte personnel. Vous pourrez ajouter vos entreprises (recherche automatique SIRET ou saisie manuelle) depuis votre espace après connexion.</span>
                </div>
              </>
            )}
            <div><Label>Email *</Label><Input data-testid="portal-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" /></div>
            {mode !== "forgot" && (
              <div><Label>Mot de passe *</Label><Input data-testid="portal-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} autoComplete={mode === "login" ? "current-password" : "new-password"} /></div>
            )}

            <Button type="submit" disabled={busy} data-testid="portal-submit" className="w-full bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold py-5">
              {busy
                ? <><Loader2 className="animate-spin mr-2" size={16} />Patience…</>
                : (mode === "login"
                    ? <><LogIn size={16} className="mr-2" />Se connecter</>
                    : mode === "forgot"
                      ? <><KeyRound size={16} className="mr-2" />Envoyer le lien</>
                      : <><UserPlus size={16} className="mr-2" />Créer mon espace</>)}
            </Button>

            {mode === "login" && (
              <div className="flex items-center justify-between gap-2 text-xs">
                <button type="button" onClick={() => setMode("forgot")} className="text-[#06B6D4] font-bold hover:underline inline-flex items-center gap-1" data-testid="portal-forgot-link"><KeyRound size={12} />Mot de passe oublié ?</button>
              </div>
            )}

            {mode === "login" && (
              <div className="flex items-center gap-2 text-[11px] text-[#64748B] bg-[#FAFAFA] rounded-lg p-2.5 border border-black/5">
                <ShieldCheck size={14} className="text-[#06B6D4] shrink-0" />
                <span>L'admin se connecte ici avec ses identifiants — détection automatique du rôle.</span>
              </div>
            )}

            <div className="text-center text-sm text-[#475569]">
              {mode === "login" ? (
                <>Pas encore de compte ? <button type="button" onClick={() => setMode("register")} className="text-[#06B6D4] font-bold hover:underline" data-testid="portal-switch-register">Créer un espace</button></>
              ) : (
                <>{mode === "forgot" ? "Vous vous souvenez ?" : "Déjà inscrit ?"} <button type="button" onClick={() => setMode("login")} className="text-[#06B6D4] font-bold hover:underline" data-testid="portal-switch-login">Se connecter</button></>
              )}
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}
